import { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, FileText, CheckCircle, XCircle, Clock, AlertCircle, User, MessageSquare } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const GestionDemandesAbsence = ({ user }) => {
  const [demandes, setDemandes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('en_attente');
  const [selectedDemande, setSelectedDemande] = useState(null);
  const [reponseMotif, setReponseMotif] = useState('');

  useEffect(() => {
    loadDemandes();
  }, []);

  const loadDemandes = async () => {
    try {
      const response = await axios.get('/demandes-absence');
      setDemandes(response.data || []);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleRepondre = async (demandeId, statut) => {
    if (!reponseMotif.trim()) {
      toast.error('Veuillez saisir un motif de réponse');
      return;
    }

    try {
      await axios.put(`/demandes-absence/${demandeId}`, {
        statut,
        reponse_motif: reponseMotif
      });
      
      toast.success(statut === 'acceptee' ? 'Demande acceptée' : 'Demande refusée');
      setSelectedDemande(null);
      setReponseMotif('');
      loadDemandes();
    } catch (error) {
      toast.error('Erreur lors de la réponse');
    }
  };

  const filteredDemandes = demandes.filter(d => {
    if (filter === 'all') return true;
    return d.statut === filter;
  });

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'acceptee':
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 text-sm font-medium rounded-full">
            <CheckCircle className="h-4 w-4" /> Acceptée
          </span>
        );
      case 'refusee':
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-700 text-sm font-medium rounded-full">
            <XCircle className="h-4 w-4" /> Refusée
          </span>
        );
      default:
        return (
          <span className="flex items-center gap-1 px-3 py-1 bg-orange-100 text-orange-700 text-sm font-medium rounded-full animate-pulse">
            <Clock className="h-4 w-4" /> En attente
          </span>
        );
    }
  };

  const countByStatus = (status) => demandes.filter(d => d.statut === status).length;

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="gestion-demandes-absence">
      <div>
        <h1 className="text-3xl font-display font-bold text-[#0F172A]">Demandes d'Absence</h1>
        <p className="text-slate-600">Gérer les demandes d'absence justifiée des stagiaires</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <button
          onClick={() => setFilter('en_attente')}
          className={`p-4 rounded-lg border-2 transition-all ${filter === 'en_attente' ? 'border-orange-500 bg-orange-50' : 'border-slate-200 bg-white hover:border-orange-300'}`}
          data-testid="filter-en-attente"
        >
          <div className="flex items-center gap-3">
            <Clock className="h-8 w-8 text-orange-500" />
            <div className="text-left">
              <p className="text-2xl font-bold text-orange-600">{countByStatus('en_attente')}</p>
              <p className="text-sm text-slate-600">En attente</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => setFilter('acceptee')}
          className={`p-4 rounded-lg border-2 transition-all ${filter === 'acceptee' ? 'border-green-500 bg-green-50' : 'border-slate-200 bg-white hover:border-green-300'}`}
          data-testid="filter-acceptee"
        >
          <div className="flex items-center gap-3">
            <CheckCircle className="h-8 w-8 text-green-500" />
            <div className="text-left">
              <p className="text-2xl font-bold text-green-600">{countByStatus('acceptee')}</p>
              <p className="text-sm text-slate-600">Acceptées</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => setFilter('refusee')}
          className={`p-4 rounded-lg border-2 transition-all ${filter === 'refusee' ? 'border-red-500 bg-red-50' : 'border-slate-200 bg-white hover:border-red-300'}`}
          data-testid="filter-refusee"
        >
          <div className="flex items-center gap-3">
            <XCircle className="h-8 w-8 text-red-500" />
            <div className="text-left">
              <p className="text-2xl font-bold text-red-600">{countByStatus('refusee')}</p>
              <p className="text-sm text-slate-600">Refusées</p>
            </div>
          </div>
        </button>
        <button
          onClick={() => setFilter('all')}
          className={`p-4 rounded-lg border-2 transition-all ${filter === 'all' ? 'border-blue-500 bg-blue-50' : 'border-slate-200 bg-white hover:border-blue-300'}`}
          data-testid="filter-all"
        >
          <div className="flex items-center gap-3">
            <Calendar className="h-8 w-8 text-blue-500" />
            <div className="text-left">
              <p className="text-2xl font-bold text-blue-600">{demandes.length}</p>
              <p className="text-sm text-slate-600">Total</p>
            </div>
          </div>
        </button>
      </div>

      {/* Liste des demandes */}
      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50">
          <h2 className="font-semibold text-[#0F172A]">
            {filter === 'all' ? 'Toutes les demandes' : 
             filter === 'en_attente' ? 'Demandes en attente' :
             filter === 'acceptee' ? 'Demandes acceptées' : 'Demandes refusées'}
            <span className="ml-2 text-sm font-normal text-slate-500">({filteredDemandes.length})</span>
          </h2>
        </div>

        {filteredDemandes.length === 0 ? (
          <div className="text-center py-12">
            <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucune demande</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-200">
            {filteredDemandes.map((demande) => (
              <div 
                key={demande.demande_id} 
                className={`p-4 hover:bg-slate-50 transition-colors ${selectedDemande === demande.demande_id ? 'bg-blue-50' : ''}`}
                data-testid={`demande-${demande.demande_id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-slate-400" />
                        <span className="font-semibold text-[#0F172A]">{demande.stagiaire_nom || 'Stagiaire'}</span>
                      </div>
                      {getStatusBadge(demande.statut)}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-slate-600 mb-2">
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {new Date(demande.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                      <span className="text-slate-400">•</span>
                      <span>Demandé le {new Date(demande.created_at).toLocaleDateString('fr-FR')}</span>
                    </div>
                    
                    <p className="text-sm text-slate-700 bg-slate-100 p-3 rounded-lg mb-2">
                      <strong>Motif:</strong> {demande.motif}
                    </p>
                    
                    {demande.justificatif && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const link = document.createElement('a');
                          link.href = demande.justificatif;
                          link.download = demande.justificatif_name || 'justificatif';
                          link.click();
                        }}
                        className="mb-2"
                        data-testid="btn-voir-justificatif"
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Voir le justificatif
                      </Button>
                    )}
                    
                    {/* Réponse du Centre si déjà traitée */}
                    {demande.statut !== 'en_attente' && demande.reponse_motif && (
                      <div className={`mt-2 p-3 rounded-lg ${demande.statut === 'acceptee' ? 'bg-green-100' : 'bg-red-100'}`}>
                        <p className="text-sm font-medium text-slate-700">
                          <MessageSquare className="h-4 w-4 inline mr-1" />
                          Réponse: {demande.reponse_motif}
                        </p>
                      </div>
                    )}
                    
                    {/* Formulaire de réponse si en attente */}
                    {demande.statut === 'en_attente' && selectedDemande === demande.demande_id && (
                      <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                          Motif de la décision *
                        </label>
                        <textarea
                          value={reponseMotif}
                          onChange={(e) => setReponseMotif(e.target.value)}
                          placeholder="Ex: Motif valable, absence justifiée / Motif non recevable..."
                          rows={2}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400 mb-3"
                          data-testid="input-reponse-motif"
                        />
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleRepondre(demande.demande_id, 'acceptee')}
                            className="bg-green-500 hover:bg-green-600 text-white"
                            data-testid="btn-accepter"
                          >
                            <CheckCircle className="h-4 w-4 mr-1" />
                            Accepter
                          </Button>
                          <Button
                            onClick={() => handleRepondre(demande.demande_id, 'refusee')}
                            className="bg-red-500 hover:bg-red-600 text-white"
                            data-testid="btn-refuser"
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Refuser
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => { setSelectedDemande(null); setReponseMotif(''); }}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Bouton Répondre si en attente */}
                  {demande.statut === 'en_attente' && selectedDemande !== demande.demande_id && (
                    <Button
                      onClick={() => setSelectedDemande(demande.demande_id)}
                      className="bg-blue-500 hover:bg-blue-600 text-white shrink-0"
                      data-testid="btn-repondre"
                    >
                      Répondre
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default GestionDemandesAbsence;
